import React from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Drawer,
  FormControlLabel, IconButton, InputAdornment, LinearProgress, MenuItem, Pagination,
  Stack, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { AnimatedSection } from '../../components/animations/MotionContainers'
import { useAuth } from '../../context/AuthContext'
import { API_REQUESTS_PAUSED } from '../../data'
import {
  cancelIntegrationExecution, createIntegrationExecution, getIntegrationExecutionDetails, getIntegrationExecutions,
  getIntegrationOpenErrors, getIntegrations, resolveIntegrationError, updateIntegrationSchedule,
} from '../../services/admin/integrationsApi'
import { getApiErrorMessage, getPaginatedItems, formatDateTime } from '../../utils/apiData'
import { getIntegrationTargets, hasIntegrationRunner } from '../../utils/integrationCatalog'
import { formatIntegrationSchedule } from '../../utils/integrationSchedule'
import { formToSchedule, scheduleToForm } from '../../utils/integrationScheduleForm'
import { getAdminApplicationCode, tienePermiso } from '../../utils/permisos'

const number = (value) => new Intl.NumberFormat('es-ES').format(Number(value) || 0)
const isRunning = (execution) => ['PENDING', 'RUNNING', 'CANCEL_REQUESTED'].includes(execution?.status)
const latestExecution = (integration) => integration.executions?.[0]

const status = (execution) => {
  switch (execution?.status) {
    case 'SUCCESS': return { label: 'Correcta', color: 'success' }
    case 'PARTIAL': return { label: 'Parcial', color: 'warning' }
    case 'FAILED': return { label: 'Con error', color: 'error' }
    case 'RUNNING': return { label: 'En curso', color: 'info' }
    case 'PENDING': return { label: 'En cola', color: 'info' }
    case 'CANCEL_REQUESTED': return { label: 'Cancelando', color: 'warning' }
    case 'CANCELLED': return { label: 'Cancelada', color: 'default' }
    default: return { label: 'Sin ejecuciones', color: 'default' }
  }
}

const nextSchedule = (integration) => (
  (integration.schedules || [])
    .filter((schedule) => schedule.active !== false && schedule.nextRunAt)
    .sort((a, b) => new Date(a.nextRunAt) - new Date(b.nextRunAt))[0]
)

const ScheduleEditorDialog = ({ integration, onClose, onSave }) => {
  const [form, setForm] = React.useState(() => scheduleToForm(integration.schedules))
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')
  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  const submit = async (event) => {
    event.preventDefault()
    setError('')
    try {
      const schedule = formToSchedule(form)
      setSaving(true)
      await onSave(integration.id, schedule)
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, submitError.message || 'No se ha podido guardar la programación.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={submit}>
        <DialogTitle sx={{ pr: 6 }}>
          Programación
          <IconButton onClick={onClose} disabled={saving} aria-label="Cerrar programación" sx={{ position: 'absolute', right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>{integration.name}</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Stack spacing={2}>
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(event) => change('enabled', event.target.checked)} />} label="Ejecución automática" />
            {form.enabled && <>
              <ToggleButtonGroup exclusive size="small" value={form.mode} onChange={(_event, value) => { if (value) change('mode', value) }} sx={{ flexWrap: 'wrap' }} aria-label="Tipo de programación">
                <ToggleButton value="INTERVAL">Intervalo</ToggleButton>
                <ToggleButton value="DAILY">Cada día</ToggleButton>
                <ToggleButton value="WEEKLY">Cada semana</ToggleButton>
                {form.mode === 'CRON' && <ToggleButton value="CRON">Personalizada</ToggleButton>}
              </ToggleButtonGroup>
              {form.mode === 'INTERVAL' && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField label="Cada" type="number" value={form.amount} onChange={(event) => change('amount', event.target.value)} slotProps={{ htmlInput: { min: 1, step: 1 } }} required fullWidth />
                <TextField select label="Unidad" value={form.unit} onChange={(event) => change('unit', event.target.value)} fullWidth>
                  <MenuItem value="MINUTES">Minutos</MenuItem>
                  <MenuItem value="HOURS">Horas</MenuItem>
                  <MenuItem value="DAYS">Días</MenuItem>
                </TextField>
              </Stack>}
              {(form.mode === 'DAILY' || form.mode === 'WEEKLY') && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                {form.mode === 'WEEKLY' && <TextField select label="Día" value={form.weekday} onChange={(event) => change('weekday', event.target.value)} fullWidth>
                  {['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'].map((day, index) => <MenuItem key={day} value={String(index)}>{day}</MenuItem>)}
                </TextField>}
                <TextField label="Hora del servidor" type="time" value={form.time} onChange={(event) => change('time', event.target.value)} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
              </Stack>}
              {form.mode === 'CRON' && <TextField label="Expresión cron" value={form.cron} onChange={(event) => change('cron', event.target.value)} fullWidth />}
            </>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export const AdminIntegrations = () => {
  const { session } = useAuth()
  const adminCode = getAdminApplicationCode(session)
  const canRun = tienePermiso(session, adminCode, 'INTEGRATIONS_WRITE')
  const [integrations, setIntegrations] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [notice, setNotice] = React.useState('')
  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState('ALL')
  const [selectedId, setSelectedId] = React.useState(null)
  const [tab, setTab] = React.useState(0)
  const [historyPage, setHistoryPage] = React.useState(1)
  const [historyVersion, setHistoryVersion] = React.useState(0)
  const [history, setHistory] = React.useState([])
  const [historyMeta, setHistoryMeta] = React.useState({ total: 0, totalPages: 0 })
  const [historyLoading, setHistoryLoading] = React.useState(false)
  const [historyError, setHistoryError] = React.useState('')
  const [errorsPage, setErrorsPage] = React.useState(1)
  const [openErrors, setOpenErrors] = React.useState([])
  const [openErrorsMeta, setOpenErrorsMeta] = React.useState({ total: 0, totalPages: 0 })
  const [openErrorsLoading, setOpenErrorsLoading] = React.useState(false)
  const [openErrorsError, setOpenErrorsError] = React.useState('')
  const [errorsVersion, setErrorsVersion] = React.useState(0)
  const [confirmError, setConfirmError] = React.useState(null)
  const [detail, setDetail] = React.useState(null)
  const [detailLoading, setDetailLoading] = React.useState(false)
  const detailRequest = React.useRef(0)
  const historyTopRef = React.useRef(null)
  const [confirmIntegration, setConfirmIntegration] = React.useState(null)
  const [confirmCancellation, setConfirmCancellation] = React.useState(null)
  const [scheduleEditorId, setScheduleEditorId] = React.useState(null)
  const [submitting, setSubmitting] = React.useState(false)

  const loadIntegrations = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await getIntegrations({ page: 1, limit: 500 })
      setIntegrations(getPaginatedItems(response))
      setErrorMessage('')
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'No se han podido cargar las integraciones.'))
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    if (!API_REQUESTS_PAUSED) loadIntegrations()
  }, [loadIntegrations])

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return
      loadIntegrations(true)
      if (selectedId) {
        if (tab === 1) setHistoryVersion((value) => value + 1)
        if (tab === 2) setErrorsVersion((value) => value + 1)
      }
    }, 5000)
    return () => window.clearInterval(timer)
  }, [loadIntegrations, selectedId, tab])

  React.useEffect(() => {
    if (!selectedId) return undefined
    let current = true
    setHistoryLoading(true)
    setHistoryError('')
    getIntegrationExecutions(selectedId, { page: historyPage, limit: 20 })
      .then((response) => {
        if (!current) return
        setHistory(getPaginatedItems(response))
        setHistoryMeta(response.meta || { total: 0, totalPages: 0 })
      })
      .catch((error) => {
        if (current) setHistoryError(getApiErrorMessage(error, 'No se ha podido cargar el historial.'))
      })
      .finally(() => { if (current) setHistoryLoading(false) })
    return () => { current = false }
  }, [selectedId, historyPage, historyVersion])

  React.useEffect(() => {
    if (!selectedId || tab !== 2) return undefined
    let current = true
    setOpenErrorsLoading(true)
    setOpenErrorsError('')
    getIntegrationOpenErrors(selectedId, { page: errorsPage, limit: 20 })
      .then((response) => {
        if (!current) return
        setOpenErrors(getPaginatedItems(response))
        setOpenErrorsMeta(response.meta || { total: 0, totalPages: 0 })
      })
      .catch((error) => {
        if (current) setOpenErrorsError(getApiErrorMessage(error, 'No se han podido cargar los errores pendientes.'))
      })
      .finally(() => { if (current) setOpenErrorsLoading(false) })
    return () => { current = false }
  }, [selectedId, tab, errorsPage, errorsVersion])

  const selected = integrations.find((integration) => String(integration.id) === String(selectedId))
  const scheduleEditor = integrations.find((integration) => String(integration.id) === String(scheduleEditorId))
  const visible = integrations.filter((integration) => {
    const term = search.trim().toLowerCase()
    const matchesSearch = !term || [integration.name, integration.code, integration.system]
      .some((value) => String(value || '').toLowerCase().includes(term))
    const latest = latestExecution(integration)
    const matchesFilter = filter === 'ALL'
      || (filter === 'ERRORS' && ['FAILED', 'PARTIAL'].includes(latest?.status))
      || (filter === 'RUNNING' && isRunning(latest))
    return matchesSearch && matchesFilter
  })

  const openIntegration = (integration, initialTab = 0) => {
    detailRequest.current += 1
    setSelectedId(integration.id)
    setTab(initialTab)
    setHistoryPage(1)
    setHistory([])
    setHistoryMeta({ total: 0, totalPages: 0 })
    setErrorsPage(1)
    setOpenErrors([])
    setOpenErrorsMeta({ total: 0, totalPages: 0 })
    setDetail(null)
    setDetailLoading(false)
  }

  const closeIntegration = () => {
    detailRequest.current += 1
    setSelectedId(null)
    setDetail(null)
    setDetailLoading(false)
  }

  const showDetails = async (execution) => {
    const request = ++detailRequest.current
    if (String(detail?.id) === String(execution.id)) {
      setDetail(null)
      return
    }
    setDetail(null)
    setDetailLoading(true)
    setHistoryError('')
    try {
      const response = await getIntegrationExecutionDetails(selectedId, execution.id)
      if (request === detailRequest.current) {
        setDetail(response)
        historyTopRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
      }
    } catch (error) {
      if (request === detailRequest.current) setHistoryError(getApiErrorMessage(error, 'No se ha podido cargar el detalle de la ejecución.'))
    } finally {
      if (request === detailRequest.current) setDetailLoading(false)
    }
  }

  const runManual = async () => {
    if (!confirmIntegration || submitting) return
    setSubmitting(true)
    setErrorMessage('')
    try {
      const execution = await createIntegrationExecution(confirmIntegration.id)
      setNotice(`Ejecución manual #${execution.id} enviada a la cola.`)
      setConfirmIntegration(null)
      await loadIntegrations(true)
      if (selectedId) setHistoryVersion((value) => value + 1)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'No se ha podido lanzar la sincronización.'))
      setConfirmIntegration(null)
    } finally {
      setSubmitting(false)
    }
  }

  const cancelRun = async () => {
    if (!confirmCancellation || submitting) return
    setSubmitting(true)
    setErrorMessage('')
    try {
      const result = await cancelIntegrationExecution(confirmCancellation.integration.id, confirmCancellation.execution.id)
      setNotice(result.status === 'CANCELLED' ? 'Ejecución cancelada.' : 'Cancelación solicitada. La ejecución se detendrá en el siguiente punto de control.')
      setConfirmCancellation(null)
      await loadIntegrations(true)
      if (selectedId) setHistoryVersion((value) => value + 1)
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, 'No se ha podido cancelar la ejecución.'))
      setConfirmCancellation(null)
    } finally {
      setSubmitting(false)
    }
  }

  const saveSchedule = async (id, schedule) => {
    const updated = await updateIntegrationSchedule(id, schedule)
    setIntegrations((current) => current.map((item) => String(item.id) === String(id) ? updated : item))
    setNotice('Programación actualizada.')
    setScheduleEditorId(null)
  }

  const resolveError = async () => {
    if (!confirmError || submitting) return
    setSubmitting(true)
    try {
      await resolveIntegrationError(selectedId, confirmError.id)
      setConfirmError(null)
      setErrorsVersion((value) => value + 1)
      setNotice('Error marcado como resuelto.')
    } catch (error) {
      setOpenErrorsError(getApiErrorMessage(error, 'No se ha podido resolver el error.'))
      setConfirmError(null)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box className="page-fade">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Integraciones</Typography>
          <Typography>Sincronizaciones con Business Central</Typography>
        </Box>
        <Tooltip title="Actualizar integraciones">
          <span><IconButton onClick={() => loadIntegrations()} disabled={loading} aria-label="Actualizar integraciones"><RefreshRoundedIcon /></IconButton></span>
        </Tooltip>
      </Box>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            <Typography><strong>{integrations.length}</strong> integraciones</Typography>
            <Typography><strong>{integrations.filter((item) => nextSchedule(item)).length}</strong> programadas</Typography>
            <Typography color="error.main"><strong>{integrations.filter((item) => ['PARTIAL', 'FAILED'].includes(latestExecution(item)?.status)).length}</strong> con incidencias</Typography>
          </Stack>
          <TextField size="small" placeholder="Buscar integración" value={search} onChange={(event) => setSearch(event.target.value)}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> } }} />
        </Stack>
        <Tabs value={filter} onChange={(_event, value) => setFilter(value)} sx={{ mb: 1 }}>
          <Tab value="ALL" label="Todas" />
          <Tab value="ERRORS" label="Con incidencias" />
          <Tab value="RUNNING" label="En curso" />
        </Tabs>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {errorMessage && <Alert severity="error" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 1.5 }}>{notice}</Alert>}
        <Box className="activity-table-wrap admin-integrations-table-wrap">
          <Table size="small" sx={{ minWidth: 1180, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '26%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '11%' }} />
            </colgroup>
            <TableHead><TableRow>
              <TableCell>Integración</TableCell>
              <TableCell>Tablas destino</TableCell>
              <TableCell>Última ejecución</TableCell>
              <TableCell>Próxima ejecución</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acción</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {visible.map((integration) => {
                const latest = latestExecution(integration)
                const activeExecution = isRunning(latest) ? latest : null
                const schedule = nextSchedule(integration)
                const targets = getIntegrationTargets(integration)
                const overdue = schedule && new Date(schedule.nextRunAt).getTime() < Date.now()
                const runnable = hasIntegrationRunner(integration.code)
                return (
                  <TableRow key={integration.id} hover>
                    <TableCell>
                      <Button variant="text" onClick={() => openIntegration(integration)} sx={{ p: 0, textAlign: 'left', justifyContent: 'flex-start', whiteSpace: 'normal', lineHeight: 1.35 }}>
                        {integration.name}
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{integration.code}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2">{targets.length ? targets.map((item) => item.label).join(' · ') : 'Sin mapeo definido'}</Typography>
                      {targets.length > 0 && <Button size="small" onClick={() => openIntegration(integration)} sx={{ p: 0, minWidth: 0 }}>Ver tablas</Button>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDateTime(latest?.startedAt || latest?.finishedAt, latest ? 'En cola' : 'Nunca')}</Typography>
                      {latest && <Typography variant="caption" display="block" color="text.secondary">{number(latest.recordsRead)} leídos · {number(latest.recordsFailed)} fallidos</Typography>}
                      <Box sx={{ mt: 1 }}>
                        <Button size="small" startIcon={<HistoryRoundedIcon fontSize="small" />} onClick={() => openIntegration(integration, 1)}
                          sx={{ p: 0, minWidth: 0, display: 'flex', justifyContent: 'flex-start', textAlign: 'left', '& .MuiButton-startIcon': { ml: 0, mr: 0.5 } }}>
                          Ver ejecuciones
                        </Button>
                        <Button size="small" color="error" onClick={() => openIntegration(integration, 2)}
                          sx={{ p: 0, minWidth: 0, display: 'flex', justifyContent: 'flex-start', textAlign: 'left' }}>
                          Errores pendientes
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDateTime(schedule?.nextRunAt, 'Sin programar')}</Typography>
                      <Typography variant="caption" display="block" color={overdue ? 'warning.main' : 'text.secondary'}>{overdue ? 'Atrasada · ' : ''}{formatIntegrationSchedule(schedule)}</Typography>
                      {canRun && <Tooltip title="Editar programación"><IconButton size="small" aria-label={`Editar programación de ${integration.name}`} onClick={() => setScheduleEditorId(integration.id)}><EditCalendarRoundedIcon fontSize="small" /></IconButton></Tooltip>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={status(latest).color} label={status(latest).label} />
                      {!integration.active && <Typography variant="caption" display="block" color="text.secondary">Inactiva</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      {canRun && activeExecution && (
                        <Button size="small" variant="outlined" color="warning" startIcon={<StopCircleRoundedIcon />}
                          disabled={submitting || activeExecution.status === 'CANCEL_REQUESTED'}
                          onClick={() => setConfirmCancellation({ integration, execution: activeExecution })}>
                          {activeExecution.status === 'CANCEL_REQUESTED' ? 'Cancelando' : 'Cancelar'}
                        </Button>
                      )}
                      {canRun && runnable && !activeExecution && (
                        <Button size="small" variant="outlined" startIcon={<PlayArrowRoundedIcon />}
                          disabled={!integration.active || submitting}
                          onClick={() => setConfirmIntegration(integration)}>Ejecutar</Button>
                      )}
                      {!runnable && <Typography variant="caption" color="text.secondary">Sin sincronizador</Typography>}
                    </TableCell>
                  </TableRow>
                )
              })}
              {!loading && visible.length === 0 && <TableRow><TableCell colSpan={6}>No hay integraciones para este filtro.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>

      <Drawer anchor="right" open={Boolean(selectedId)} onClose={closeIntegration}
        slotProps={{ paper: { sx: { width: 'min(820px, 100vw)', p: { xs: 2, sm: 3 } } } }}>
        {selected && <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6">{selected.name}</Typography>
              <Typography variant="body2" color="text.secondary">{selected.code}</Typography>
            </Box>
            <Tooltip title="Cerrar"><IconButton onClick={closeIntegration} aria-label="Cerrar detalle"><CloseRoundedIcon /></IconButton></Tooltip>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 2, mb: 1 }}>
            <Chip size="small" color={selected.active ? 'success' : 'default'} label={selected.active ? 'Activa' : 'Inactiva'} />
            <Typography variant="body2" color="text.secondary">{selected.system} · {selected.direction}</Typography>
          </Stack>
          <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
            <Tab label={`Tablas (${getIntegrationTargets(selected).length})`} />
            <Tab label={`Ejecuciones (${historyMeta.total})`} />
            <Tab label={`Errores pendientes (${openErrorsMeta.total})`} />
          </Tabs>
          {tab === 0 && <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Table size="small" sx={{ minWidth: 550 }}>
              <TableHead><TableRow><TableCell>Dato</TableCell><TableCell>Tabla Suite</TableCell><TableCell>Origen BC</TableCell></TableRow></TableHead>
              <TableBody>
                {getIntegrationTargets(selected).map((target) => (
                  <TableRow key={target.table}>
                    <TableCell>{target.label}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{target.table}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{target.endpoint || 'Derivado'}</TableCell>
                  </TableRow>
                ))}
                {!getIntegrationTargets(selected).length && <TableRow><TableCell colSpan={3}>No hay tablas documentadas para esta integración.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>}
          {tab === 1 && <Box ref={historyTopRef} sx={{ mt: 2 }}>
            {historyLoading && <LinearProgress sx={{ mb: 1 }} />}
            {historyError && <Alert severity="error" sx={{ mb: 1 }}>{historyError}</Alert>}
            {detailLoading && <LinearProgress sx={{ mb: 2 }} />}
            {detail && <Box component="section" aria-label={`Detalle de ejecución ${detail.id}`}
              sx={{ borderLeft: 3, borderColor: detail.recordsFailed ? 'error.main' : 'primary.main', bgcolor: 'action.hover', px: 2, py: 1.5, mb: 3 }}>
              <Typography variant="overline" color="text.secondary">Detalle seleccionado</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>Ejecución #{detail.id}</Typography>
                <Chip size="small" color={status(detail).color} label={status(detail).label} />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Inicio: {formatDateTime(detail.startedAt, 'En cola')} · Fin: {formatDateTime(detail.finishedAt, 'Pendiente')} · {number(detail.recordsFailed)} registros fallidos
              </Typography>
              {detail.errorSummary && <Alert severity="error" sx={{ mb: 1 }}>{detail.errorSummary}</Alert>}
              {detail.alerts?.map((alert) => <Alert key={alert.id} severity={alert.severity === 'ERROR' || alert.severity === 'CRITICAL' ? 'error' : 'warning'} sx={{ mb: 1 }}>{alert.message}</Alert>)}
              {detail.errors?.length > 0 && <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Errores de esta ejecución ({number(detail.errorsCount ?? detail.errors.length)})</Typography>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label={`Errores de la ejecución ${detail.id}`} sx={{ minWidth: 560 }}>
                    <TableHead><TableRow>
                      <TableCell sx={{ width: '22%' }}>Operación</TableCell>
                      <TableCell sx={{ width: '23%' }}>Registro</TableCell>
                      <TableCell>Mensaje de error</TableCell>
                    </TableRow></TableHead>
                    <TableBody>{detail.errors.map((error) => (
                      <TableRow key={error.id}>
                        <TableCell>{error.operation || 'Registro'}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{error.externalKey || '—'}</TableCell>
                        <TableCell sx={{ overflowWrap: 'anywhere' }}>{error.message || error.code || 'Error sin detalle'}</TableCell>
                      </TableRow>
                    ))}</TableBody>
                  </Table>
                </Box>
              </>}
              {detail.errorsCount > detail.errors?.length && <Typography variant="caption" color="text.secondary">Mostrando los 50 errores más recientes de {number(detail.errorsCount)}.</Typography>}
              {!detail.errorSummary && !detail.errors?.length && !detail.alerts?.length && <Typography variant="body2">No se registraron errores en esta ejecución.</Typography>}
            </Box>}
            <Box component="section" aria-label="Historial de ejecuciones">
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>Historial de ejecuciones</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{number(historyMeta.total)} ejecuciones registradas</Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 670 }}>
                <TableHead><TableRow>
                  <TableCell>Inicio</TableCell><TableCell>Origen</TableCell><TableCell>Estado</TableCell>
                  <TableCell>Registros</TableCell><TableCell>Incidencia</TableCell><TableCell align="right">Detalle</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {history.map((execution) => (
                    <TableRow key={execution.id} selected={String(detail?.id) === String(execution.id)}>
                      <TableCell>{formatDateTime(execution.startedAt || execution.finishedAt, 'En cola')}</TableCell>
                      <TableCell>{execution.triggerType === 'MANUAL' ? 'Manual' : 'Programada'}</TableCell>
                      <TableCell><Chip size="small" color={status(execution).color} label={status(execution).label} /></TableCell>
                      <TableCell>
                        <Typography variant="body2">{number(execution.recordsRead)} leídos</Typography>
                        <Typography variant="caption" color="text.secondary">{number(execution.recordsCreated)} nuevos · {number(execution.recordsUpdated)} actualizados</Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, overflowWrap: 'anywhere' }}>{execution.errorSummary || (execution.recordsFailed ? `${number(execution.recordsFailed)} fallidos` : '—')}</TableCell>
                      <TableCell align="right"><Tooltip title="Ver detalle y errores"><IconButton size="small" aria-label={`Ver ejecución ${execution.id}`} onClick={() => showDetails(execution)}><VisibilityRoundedIcon fontSize="small" /></IconButton></Tooltip></TableCell>
                    </TableRow>
                  ))}
                  {!historyLoading && !history.length && <TableRow><TableCell colSpan={6}>Sin ejecuciones registradas.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Box>
            {historyMeta.totalPages > 1 && <Pagination page={historyPage} count={historyMeta.totalPages} onChange={(_event, page) => { detailRequest.current += 1; setHistoryPage(page); setDetail(null); setDetailLoading(false) }} sx={{ mt: 2 }} />}
            </Box>
          </Box>}
          {tab === 2 && <Box component="section" aria-label="Errores pendientes" sx={{ mt: 2 }}>
            {openErrorsLoading && <LinearProgress sx={{ mb: 1 }} />}
            {openErrorsError && <Alert severity="error" sx={{ mb: 1 }}>{openErrorsError}</Alert>}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 660, tableLayout: 'fixed' }}>
                <TableHead><TableRow>
                  <TableCell sx={{ width: '16%' }}>Fecha</TableCell>
                  <TableCell sx={{ width: '16%' }}>Operación</TableCell>
                  <TableCell sx={{ width: '18%' }}>Registro</TableCell>
                  <TableCell>Incidencia</TableCell>
                  <TableCell sx={{ width: '12%' }}>Ejecución</TableCell>
                  {canRun && <TableCell align="right" sx={{ width: '15%' }}>Acción</TableCell>}
                </TableRow></TableHead>
                <TableBody>
                  {openErrors.map((item) => <TableRow key={item.id}>
                    <TableCell>{formatDateTime(item.occurredAt, '—')}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.operation || 'Registro'}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.externalKey || 'Sin identificador'}</TableCell>
                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.message || item.code || 'Error sin detalle'}</TableCell>
                    <TableCell>#{item.executionId}</TableCell>
                    {canRun && <TableCell align="right">
                      <Tooltip title="Marcar resuelto manualmente">
                        <IconButton size="small" aria-label={`Marcar error ${item.id} como resuelto`} onClick={() => setConfirmError(item)}>
                          <CheckCircleOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>}
                  </TableRow>)}
                  {!openErrorsLoading && openErrors.length === 0 && <TableRow><TableCell colSpan={canRun ? 6 : 5}>No hay errores pendientes.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Box>
            {openErrorsMeta.totalPages > 1 && <Pagination page={errorsPage} count={openErrorsMeta.totalPages}
              onChange={(_event, page) => setErrorsPage(page)} sx={{ mt: 2 }} />}
          </Box>}
        </>}
      </Drawer>
      {scheduleEditor && <ScheduleEditorDialog key={scheduleEditor.id} integration={scheduleEditor} onClose={() => setScheduleEditorId(null)} onSave={saveSchedule} />}

      <Dialog open={Boolean(confirmIntegration)} onClose={() => { if (!submitting) setConfirmIntegration(null) }} maxWidth="xs" fullWidth>
        <DialogTitle>Ejecutar sincronización</DialogTitle>
        <DialogContent>
          <Typography>¿Iniciar ahora {confirmIntegration?.name}? La tarea se añadirá a la cola de integraciones.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmIntegration(null)} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" startIcon={<SyncRoundedIcon />} onClick={runManual} disabled={submitting}>
            {submitting ? 'Enviando...' : 'Ejecutar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmCancellation)} onClose={() => { if (!submitting) setConfirmCancellation(null) }} maxWidth="xs" fullWidth>
        <DialogTitle>Cancelar ejecución</DialogTitle>
        <DialogContent>
          <Typography>¿Cancelar la sincronización de {confirmCancellation?.integration.name}? Los datos ya procesados se conservarán.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancellation(null)} disabled={submitting}>Volver</Button>
          <Button color="warning" variant="contained" startIcon={<StopCircleRoundedIcon />} onClick={cancelRun} disabled={submitting}>
            {submitting ? 'Cancelando...' : 'Cancelar ejecución'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmError)} onClose={() => { if (!submitting) setConfirmError(null) }} maxWidth="xs" fullWidth>
        <DialogTitle>Marcar error como resuelto</DialogTitle>
        <DialogContent>
          <Typography>¿Confirmas que se ha revisado {confirmError?.externalKey || 'este registro'}? Esta acción lo quitará de pendientes, pero conservará el historial de la ejecución.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmError(null)} disabled={submitting}>Cancelar</Button>
          <Button variant="contained" startIcon={<CheckCircleOutlineRoundedIcon />} onClick={resolveError} disabled={submitting}>
            Marcar resuelto
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
